// src/utils/buildDealFromLead.js
// Pure, Firebase-free: maps a converted buyer lead (+ confirmed property/price)
// into the `newdata` shape the `deals` collection expects. Task seeding is left
// to the caller (buildDealBackfill) so this stays pure and unit-testable.
//
// A converted deal must be SHAPE-COMPATIBLE with a normal turn-in deal so every
// deals view (board, transactions, commission breakdown, approval inbox) reads
// it safely. The financial fields are blank here and get filled in when the
// deal is reviewed/approved — exactly like a brand-new turn-in awaiting input.

import { listClients } from "./leadClients";
import { withDefaultAdminFee } from "./adminFee";
import { clientCareFieldsForNewTurnIn } from "./clientCareTurnIn";

// `teamDefaults` (Settings > Team Defaults, when the caller has it) sets the
// broker admin fee a graduated deal starts with: Henry 2026-09-18 "Always $599".
export function buildDealFromLead(
  lead = {},
  overrides = {},
  { user = {}, nowId, teamDefaults } = {},
) {
  const deal = {
    // --- Identity / routing ---
    status: "Pending",
    requestType: "saleTurnIn",
    // Henry 2026-09-19: a Team Lead's own turn-in waits for Client Care. A converted lead is saved Pending, so this
    // adds nothing today (it is on Pending Approval already); it keeps every new turn-in on the one rule.
    ...clientCareFieldsForNewTurnIn({
      submitter: user,
      status: "Pending",
      requestType: "saleTurnIn",
      now: nowId,
    }),
    dealtype: "Buyer",
    dealStage: "Under Contract",
    createdby: user.id || "",
    createdat: nowId,
    sourceLeadId: lead.id || "",

    // --- Carried over from the lead + the conversion dialog ---
    // Every client on the lead — primary AND co-clients — not just the first.
    customers: listClients(lead).map((c) => c.contactId),
    agentData: lead.assignedAgent || "",
    agentName: lead.assignedAgentName || "",
    // Prefer a resolved system lead-source OBJECT from the caller (deals store
    // the full object); fall back to the lead's raw value — buyer leads keep it
    // in `leadSource`, listing leads in `source`.
    leadsource:
      overrides.leadsource !== undefined
        ? overrides.leadsource
        : lead.leadSource || lead.source || "",
    propertyaddress: overrides.propertyaddress || "",
    offerPrice: overrides.offerPrice || "",
    // The lead's story rides along instead of being dropped at conversion.
    notes: lead.notes || "",
    financingType: lead.financingType || "",
    // Henry 10/06/2026: the buyer representation agreement's expiration date
    // (YYYY-MM-DD), shown on the deal's buyer form.
    buyerAgreementExpiration: lead.buyerAgreementExpiration || "",

    // --- Standard deal fields kept present (blank until approval/edit) so no
    //     deals view ever hits `undefined` on a converted deal ---
    closingdate: "",
    propertyphoto: [],
    propertyType: "",
    propertyLocation: null,
    earnestMoney: "",
    inspection: "",
    inspectionDeadline: "",
    financingDeadline: "",

    // Commission / split (filled in during approval)
    totalcommissionrate: "",
    agentCommission: "",
    agentCommissionamount: "",
    agentdiscount: 0,
    leadCommission: "",
    splittype: "",
    splitdescription: "",
    inhousereferral: false,
    teamleadName: "",
    assignedteamlead: "",
    brokeradminfee: overrides.brokeradminfee,

    // --- Tasks (seeded by buildDealBackfill in the caller) ---
    tasks: [],
    taskStatusTracker: {},
  };
  // A new deal: a missing fee takes the Team Defaults figure ($599 fallback).
  return withDefaultAdminFee(deal, teamDefaults);
}
